
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit3, MapPin } from "lucide-react";
import { DeliveryAddress, useDeliveryAddresses, useCreateDeliveryAddress, useUpdateDeliveryAddress } from "@/hooks/use-delivery-addresses";
import { DeliveryAddressForm } from "./DeliveryAddressForm";

interface DeliveryAddressSelectorProps {
  organizationId: string;
  selectedAddressId: string | null;
  onAddressSelect: (addressId: string | null) => void;
  className?: string;
}

export const DeliveryAddressSelector = ({
  organizationId,
  selectedAddressId,
  onAddressSelect,
  className = ""
}: DeliveryAddressSelectorProps) => {
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<DeliveryAddress | null>(null);
  
  const { data: addresses, isLoading } = useDeliveryAddresses(organizationId);
  const createAddress = useCreateDeliveryAddress();
  const updateAddress = useUpdateDeliveryAddress();

  const selectedAddress = addresses?.find(addr => addr.id === selectedAddressId);

  // Auto-select default address when addresses are loaded
  React.useEffect(() => {
    if (addresses && addresses.length > 0 && !selectedAddressId) {
      const defaultAddress = addresses.find(addr => addr.is_default);
      if (defaultAddress) {
        onAddressSelect(defaultAddress.id);
      }
    }
  }, [addresses, selectedAddressId, onAddressSelect]);

  const handleCreateAddress = async (addressData: Omit<DeliveryAddress, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const newAddress = await createAddress.mutateAsync(addressData);
      onAddressSelect(newAddress.id);
      setShowForm(false);
    } catch (error) {
      console.error('Error creating address:', error);
    }
  };

  const handleUpdateAddress = async (addressData: Omit<DeliveryAddress, 'id' | 'created_at' | 'updated_at'>) => {
    if (!editingAddress) return;
    
    try {
      await updateAddress.mutateAsync({ id: editingAddress.id, ...addressData });
      setEditingAddress(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error updating address:', error);
    }
  };

  const handleEditClick = () => {
    if (selectedAddress) {
      setEditingAddress(selectedAddress);
      setShowForm(true);
    }
  };

  const formatAddress = (address: DeliveryAddress) => {
    const parts = [
      address.address_line_1,
      address.address_line_2,
      address.city,
      address.postal_code,
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-teal-600" />
            Delivery Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Select value={selectedAddressId || ""} onValueChange={onAddressSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select delivery address" />
              </SelectTrigger>
              <SelectContent>
                {addresses?.map((address) => (
                  <SelectItem key={address.id} value={address.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{address.contact_name}</span>
                      <span className="text-sm text-muted-foreground truncate">
                        {address.company_name && `${address.company_name}, `}
                        {address.city}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedAddress && (
            <div className="p-3 bg-slate-50 rounded-lg border">
              <div className="space-y-1 text-sm">
                <div className="font-medium">{selectedAddress.contact_name}</div>
                {selectedAddress.company_name && (
                  <div className="text-muted-foreground">{selectedAddress.company_name}</div>
                )}
                <div className="text-muted-foreground">
                  {formatAddress(selectedAddress)}
                </div>
                <div className="text-muted-foreground">{selectedAddress.phone_number}</div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add New
            </Button>
            {selectedAddress && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditClick}
                className="flex items-center gap-2"
              >
                <Edit3 className="h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <DeliveryAddressForm
        open={showForm}
        onOpenChange={(open) => {
          setShowForm(open);
          if (!open) setEditingAddress(null);
        }}
        onSubmit={editingAddress ? handleUpdateAddress : handleCreateAddress}
        initialData={editingAddress || undefined}
        organizationId={organizationId}
        isLoading={createAddress.isPending || updateAddress.isPending}
      />
    </>
  );
};
